import type { PrizeDraw, PrizeOption } from "./api"

/** Which widget a prize game renders on the storefront. */
export type PrizeGameKind = "scratch" | "wheel"

/** Maps the wire `type` (`SCRATCH_CARD` | `WHEEL`, any case) to the widget; anything unknown is a scratch card. */
export function prizeGameKind(type: string | null | undefined): PrizeGameKind {
  return type?.trim().toUpperCase() === "WHEEL" ? "wheel" : "scratch"
}

/** Id of the wheel slice that stands for "try again", the share of the odds no prize covers. */
export const WHEEL_MISS_ID = "__miss__"

/** One slice of the prize wheel, in clockwise order from the pointer. */
export type WheelSegment = { id: string; label: string; tone: "primary" | "secondary" | "miss" }

const WHEEL_TURNS = 6

/** The prizes as alternating brand slices, closed by one "Não foi dessa vez" slice unless the game can never miss. */
export function wheelSegments(prizes: readonly PrizeOption[], missPossible = true): WheelSegment[] {
  const slices: WheelSegment[] = prizes.map((prize, index) => ({
    id: prize.id,
    label: prize.label,
    tone: index % 2 === 0 ? "primary" : "secondary",
  }))
  return missPossible ? [...slices, { id: WHEEL_MISS_ID, label: "Não foi dessa vez", tone: "miss" }] : slices
}

/** Index of the slice the server's draw landed on: the prize by id, then by label, else the miss slice. */
export function wheelLandingIndex(segments: readonly WheelSegment[], draw: PrizeDraw): number {
  const missIndex = Math.max(0, segments.findIndex((segment) => segment.id === WHEEL_MISS_ID))
  if (!draw.won) return missIndex
  const byId = segments.findIndex((segment) => segment.id === draw.prizeId)
  if (byId >= 0) return byId
  const byLabel = segments.findIndex((segment) => segment.id !== WHEEL_MISS_ID && segment.label === draw.prizeLabel)
  return byLabel >= 0 ? byLabel : 0
}

/** Clockwise rotation, in degrees, that stops the center of slice `index` under the top pointer. */
export function wheelLandingRotation(segmentCount: number, index: number, turns = WHEEL_TURNS): number {
  const slice = 360 / Math.max(1, segmentCount)
  return turns * 360 + (360 - (index + 0.5) * slice)
}
